/**
 * File Upload Service for hPanel Server
 * Uploads files to hPanel server instead of Supabase Storage
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin

/**
 * Upload a file to hPanel server
 * @param {File} file - The file to upload
 * @param {string} organizationId - Organization ID
 * @param {string} clientId - Client ID
 * @param {string} category - Document category
 * @returns {Promise<{success: boolean, fileUrl: string, fileName: string}>}
 */
export const uploadFileToServer = async (file, organizationId, clientId, category = 'other') => {
  try {
    // Get Supabase session token for authentication
    const { data: { session } } = await (await import('./supabase')).supabase.auth.getSession()
    if (!session) {
      throw new Error('Not authenticated')
    }

    const formData = new FormData()
    formData.append('file', file)
    formData.append('organizationId', organizationId)
    formData.append('clientId', clientId)
    formData.append('category', category)

    // On hPanel the script is typically deployed as upload-document.php under /public_html/api
    // so the public URL is: https://your-domain.com/api/upload-document.php
    // We point directly to that PHP file.
    const uploadUrl = `${API_BASE_URL}/api/upload-document.php`
    
    // Log for debugging
    console.log('Uploading to:', uploadUrl)
    console.log('API_BASE_URL:', API_BASE_URL)
    
    let response
    try {
      response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: formData,
        // Don't set Content-Type header, browser will set it with boundary
      })
    } catch (fetchError) {
      // Network error - endpoint not reachable
      console.error('Network error:', fetchError)
      throw new Error(
        `Cannot reach upload endpoint. Please check:\n` +
        `1. API endpoint exists at: ${uploadUrl}\n` +
        `2. CORS is properly configured\n` +
        `3. Server is accessible\n\n` +
        `Error: ${fetchError.message}`
      )
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ 
        message: `Upload failed: ${response.status} ${response.statusText}` 
      }))
      throw new Error(errorData.message || `Upload failed: ${response.statusText}`)
    }

    const data = await response.json()
    return {
      success: true,
      fileUrl: data.fileUrl,
      fileName: data.fileName,
      filePath: data.filePath,
    }
  } catch (error) {
    console.error('Error uploading file:', error)
    throw error
  }
}

/**
 * Download a file from hPanel server
 * @param {string} filePath - Path to the file on server
 * @param {string} fileName - Name for the downloaded file
 * @returns {Promise<Blob>}
 */
export const downloadFileFromServer = async (filePath, fileName) => {
  try {
    // Get Supabase session token for authentication
    const { data: { session } } = await (await import('./supabase')).supabase.auth.getSession()
    if (!session) {
      throw new Error('Not authenticated')
    }

    const response = await fetch(
      `${API_BASE_URL}/api/download-document.php?path=${encodeURIComponent(filePath)}`,
      {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
      },
    })

    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`)
    }

    const blob = await response.blob()
    
    // Trigger browser download
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)

    return blob
  } catch (error) {
    console.error('Error downloading file:', error)
    throw error
  }
}

/**
 * Delete a file from hPanel server
 * @param {string} filePath - Path to the file on server
 * @returns {Promise<{success: boolean}>}
 */
export const deleteFileFromServer = async (filePath) => {
  try {
    // Get Supabase session token for authentication
    const { data: { session } } = await (await import('./supabase')).supabase.auth.getSession()
    if (!session) {
      throw new Error('Not authenticated')
    }

    const response = await fetch(`${API_BASE_URL}/api/delete-document.php`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ filePath }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Delete failed' }))
      throw new Error(errorData.message || `Delete failed: ${response.statusText}`)
    }

    return { success: true }
  } catch (error) {
    console.error('Error deleting file:', error)
    throw error
  }
}

/**
 * Get a signed/preview URL for a file (if needed)
 * @param {string} filePath - Path to the file on server
 * @param {number} expiresIn - Expiration time in seconds (default 3600)
 * @returns {Promise<string>} - URL to access the file
 */
export const getFileUrl = async (filePath, expiresIn = 3600) => {
  try {
    // Get Supabase session token for authentication
    const { data: { session } } = await (await import('./supabase')).supabase.auth.getSession()
    if (!session) {
      throw new Error('Not authenticated')
    }

    const response = await fetch(
      `${API_BASE_URL}/api/file-url?path=${encodeURIComponent(filePath)}&expires=${expiresIn}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      }
    )

    if (!response.ok) {
      throw new Error(`Failed to get file URL: ${response.statusText}`)
    }

    const data = await response.json()
    return data.url
  } catch (error) {
    console.error('Error getting file URL:', error)
    throw error
  }
}

